'use client'

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react'
import type { WinTier } from 'src/components/ChipPlacementPanel/WinPill'
import { pillSideFor } from './boardGeometry'
import type { BoardColumn, BoardGeometry, Rect } from './boardGeometry'
import './birdie-board.scss'
import {
  TILE_ENTER_STEP_MS,
  TILE_WAVE_STEP_MS,
  cascadeDelaysMs,
  centreOf,
  type BoardPoint
} from './boardMotion'
import { approach, parallaxOffset, pxValue } from './boardParallax'
import type { BirdieChips } from './birdieChips'
import { BirdieTile } from './BirdieTile'
import { prefersReducedMotion } from './FlyingChip'
import { BoardHeadIcon } from './BoardHeadIcon'
import type { ChipStyle } from './chipPalette'
import { BirdieBallDefs } from './GolfBall'
import { IDLE_LIVE_STATUS, tileLife, type LiveStatus } from './liveStatus'
import type { SettlementView } from './settlement'
import { PUTTS_PER_ROUND, type BirdieTileView, type Translate } from './tileViewModel'

export interface ArcOverlayProps {
  /** The picture box on the stage. Used for two things only: `left` and `top` (B14). */
  frame: Rect
  /**
   * The layout — `boardGeometryFor(frame)` (B14). Its own `frame` is the LAYOUT
   * frame, which is also the size of the box this component scales: the two
   * have to be the same rect or the board is not registered to the picture.
   */
  geometry: BoardGeometry
  /** `frameScale(frame)`: the single number the whole board is scaled by (B14). */
  scale: number
  /**
   * The golfer capsule. It is laid out in the same reference px as everything
   * else (`cardBoxPx`) and must therefore be scaled by the same transform, so
   * the host hands it in rather than rendering it beside the overlay (B14).
   */
  children?: ReactNode
  tiles: readonly BirdieTileView[]
  placement: BirdieChips
  /**
   * The two column headings, already localised (B20 Task 2 item 6). They arrive
   * as strings rather than being built here because the i18n extractor is
   * literal-only (A25) and `t` lives on the host — `<ArcOverlay>` makes no
   * translation call of its own and never has.
   */
  headings: Readonly<Record<BoardColumn, string>>
  /** The ladder colours for a placed total — see `chipStyleForAmount`. */
  chipStyleForAmount: (amount: number) => ChipStyle
  tilesLocked: boolean
  /** B34 live ticket status, from `liveStatus(tiles, phase.livePutts)`. */
  liveStatus?: LiveStatus
  /**
   * B40: a value that changes when the board is priced for a new round — the
   * race id. It is what lets the tiles cascade in; they keep their DOM nodes
   * across rounds, so nothing else marks the moment. See `BirdieTileProps`.
   */
  enterKey?: string
  /** B40: how many putts are on screen, 0-3 — `phase.livePutts.length`. */
  puttsSeen?: number
  /**
   * B40: whether the board is tipped into the scene — `boardPhase.boardSeated`.
   *
   * It is the host's call and not this component's because it is a PHASE
   * question, and the phase is the host's: the window opens when the round
   * starts (the same one the tee-off count fills) and closes when the result is
   * on screen. Absent = flat, which is what every caller that predates it gets.
   */
  seated?: boolean
  /**
   * B40 item 8: the round's TYPE, once a putt row has revealed it, and only when
   * it is one of the two the board can draw. The stream's frost or fire reaches
   * the UI as a brief weather pass around the board's own edges — around, never
   * over the tiles, and it fades.
   *
   * `plain` is deliberately not in the type: most rounds are plain, and a board
   * that flashed for every one of them would be weather that means nothing.
   */
  roundType?: 'frost' | 'fire'
  /**
   * B40 item 8b: how many putts have DROPPED, 0-3. The weather deepens a step on
   * each one — it arrives with the reveal and then builds through the round,
   * rather than announcing itself once and leaving.
   *
   * Holed putts, not putt EVENTS: a round where nothing goes in is a round where
   * nothing is building, and a frost that thickened on three misses would be
   * telling the player something the round did not do.
   */
  weatherStep?: number
  /**
   * B32: the tiles an armed auto-bet zone passes over — `zoneMissIds` on the
   * host, empty or absent while no rule is armed. Each gets the tile's ghost.
   */
  zoneMissIds?: ReadonlySet<string>
  receivingOptionId: string | null
  /**
   * Part of the board's single prop bag (B9). The rings themselves live on
   * `<BirdieCard>` (B6, top-right of the frame, never over the green) and the
   * winning tiles are driven by `winnerIds` / `betByOptionId`, so the overlay
   * carries this without rendering it.
   */
  settlement?: SettlementView
  winnerIds: ReadonlySet<string>
  betByOptionId: ReadonlyMap<string, { won: boolean; lost: boolean; payout: number; tier: WinTier }>
  currencySymbol: string
  notice: { message: string; tone: 'warning' | 'info' | 'error' } | null
  chipLabel: (amount: number) => string
  /**
   * B38: the options whose stake is a free bet, and the localised word its
   * chip carries. Both arrive already decided — the claim map is the host's
   * and `t` lives there, exactly as with the column headings.
   */
  freeBetIds?: ReadonlySet<string>
  freeBetLabel?: string
  stakeDescription?: (input: {
    amount: number
    won: boolean
    lost: boolean
    free?: boolean
  }) => string
  onPlace: (optionId: string, target: DOMRect) => void
  onRemove: (optionId: string) => void
  /** B28: passed straight to every tile — see `BirdieTileProps`. */
  onLockedPress?: (optionId: string) => void
}

/** Clearance between the notice pill's baseline and the hole, px. */
const NOTICE_LIFT_PX = 24

/**
 * B40 item 3 — the board's drift against the stream, wired to the pointer.
 *
 * Everything expensive about a parallax is avoided by construction: the handler
 * READS NOTHING off the DOM (a viewport size is not a layout read), it writes
 * two custom properties on ONE element, and the writes are coalesced into a
 * single animation frame, so a pointer moving at 120 Hz still costs one style
 * recalculation and one composite per frame. The property it drives is a
 * `translate3d` on the frame and a lagging one on the capsule — transforms, both
 * of them, exactly like everything else B40 added.
 *
 * IT MOVES THE OPTIONS AND NOTHING ELSE (owner: "maybe that parallax should
 * only be on the betting options and not happen on the capsule etc."). Depth is
 * RELATIVE motion: a tile layer drifting against a frame, a capsule and a
 * weather edge that are all nailed down reads as far more depth than everything
 * sliding together, and it is simpler — one transformed element, one target.
 *
 * Desktop only, and not by a media query: `<ArcOverlay>` is the desktop board,
 * the phone renders `<StackBoard>`, and the phone's tighter budget is the reason
 * this is not offered there at all.
 *
 * Off entirely under `prefers-reduced-motion` — a board that follows the cursor
 * is motion the viewer did not ask for, whatever its amplitude — and the two
 * properties simply stay unset, which is the identity transform.
 */
function useBoardParallax(drift: { current: HTMLDivElement | null }): void {
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    if (prefersReducedMotion()) return undefined

    let targetX = 0
    let targetY = 0
    let currentX = 0
    let currentY = 0
    let armed = 0

    // One frame of the ease. The board does not TRACK the pointer, it moves
    // toward it — which is the difference between an overlay that feels attached
    // to the cursor and one that feels like it has weight behind glass. The loop
    // re-arms only while it is still travelling, so a settled board schedules
    // nothing at all and a still pointer costs the stream nothing.
    const step = () => {
      armed = 0
      const element = drift.current
      if (element === null) return
      const x = approach(currentX, targetX)
      const y = approach(currentY, targetY)
      currentX = x.value
      currentY = y.value
      element.style.setProperty('--birdie-par-x', pxValue(currentX))
      element.style.setProperty('--birdie-par-y', pxValue(currentY))
      if (!x.settled || !y.settled) armed = window.requestAnimationFrame(step)
    }
    const schedule = () => {
      if (armed === 0) armed = window.requestAnimationFrame(step)
    }
    const onMove = (event: PointerEvent) => {
      const offset = parallaxOffset(
        event.clientX,
        event.clientY,
        window.innerWidth,
        window.innerHeight
      )
      targetX = offset.x
      targetY = offset.y
      schedule()
    }
    // A pointer that leaves the window, and a window that loses focus, both put
    // the board back where the geometry says it is rather than leaving it parked
    // three px out for the rest of the round.
    const onRest = () => {
      targetX = 0
      targetY = 0
      schedule()
    }

    window.addEventListener('pointermove', onMove, { passive: true })
    window.addEventListener('pointerleave', onRest)
    window.addEventListener('blur', onRest)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerleave', onRest)
      window.removeEventListener('blur', onRest)
      if (armed !== 0) window.cancelAnimationFrame(armed)
    }
  }, [drift])
}
/** Stagger between the winning tiles' pills, ms (the shared panel's cascade). */
const WIN_STAGGER_MS = 80

/**
 * "two holed" / "one holed" reads better on a caption than "2 holed", and the
 * two EXACT lanes the board actually draws are exactly those two counts. The
 * `{{count}}` form is the honest general case — it fires only if a future
 * paytable puts some other count on a lane.
 *
 * Every branch spells its English string out literally: the i18n extractor is
 * literal-only (A25), so a computed key would never reach the locale files.
 */
function holedLabel(holed: number, t: Translate): string {
  if (holed === 2) return t('two holed')
  if (holed === 1) return t('one holed')
  return t('{{count}} holed', { count: holed })
}

/**
 * The price-group caption for one arc: "two holed · pays 6.46x". Every tile also
 * shows its own figure; when the trio is uniform (p = 0.5) this reads "all
 * patterns 7.60x" instead of repeating a number that looks like a fourth price.
 *
 * B15 made a price group an ARC rather than a wing — the left wing carries two
 * of them (one holed outside, two holed inside) and the right wing carries the
 * whole count market — so a caller passes `BIRDIE_BOARD_SPEC.arcs[name].keys`.
 *
 * NOT RENDERED ON THE BOARD since B13 — owner: "the little text you've added,
 * one holed - all patterns 15.07x … better to hide it, feels off and makes the
 * ui look busy". It stays exported for the rules panel, which is also what keeps
 * its five strings (and their sixteen translations) out of `i18n:cleanup`'s
 * reach: the extractor is literal-only, so the keys survive exactly as long as
 * the calls below do. (Do not write an example call in a comment here — the
 * extractor does not know what a comment is, and would extract it.)
 *
 * Returns null for an empty group — a group is empty whenever the round priced
 * none of its options.
 */
export function groupCaption(
  tiles: readonly BirdieTileView[],
  groupKeys: readonly string[],
  t: Translate
): string | null {
  const inGroup = tiles.filter((tile) => groupKeys.includes(tile.key))
  const first = inGroup[0]
  if (!first) return null
  const uniform = inGroup.every((tile) => tile.multiplierText === first.multiplierText)
  const label = holedLabel(first.made, t)
  return uniform
    ? `${label} · ${t('all patterns {{price}}', { price: first.multiplierText })}`
    : `${label} · ${t('pays {{price}}', { price: first.multiplierText })}`
}

/**
 * BEM modifier per column. A lookup rather than the column name interpolated
 * straight into the class, so a third column can never reach the stylesheet
 * without a rule to go with it (the `Record<…>` is what fails the typecheck).
 */
const HEAD_MODIFIER: Record<BoardColumn, string> = {
  count: 'count',
  exact: 'exact'
}

/**
 * Desktop composition: the two columns of tiles, their two header lines, the
 * capsule, the past-rounds rail and the winner highlight.
 *
 * B20 removed the arcs and everything that hung off them — the countdown
 * hairline (`<CountdownArc>`), the three arc paths, the lightning halo and the
 * caption placement. The capsule ring is the only countdown now, and the only
 * text on the board is the two headers below, which B20 Task 2 fills; they are
 * rendered here as empty boxes so the geometry that places them is exercised
 * (and asserted) from the day the grid lands.
 *
 * Coordinates (B14 — one layout, one transform). Everything inside
 * `.birdie-overlay__frame` is laid out in the LAYOUT frame's px and the element
 * itself is a box of exactly that size, positioned at the picture's `frame.x/y`
 * and `scale(k)`-ed from its top-left corner. So there is exactly one place the
 * picture box is consulted, exactly one transform, and no child — tile,
 * header, rail, notice or capsule — knows anything about the window size. That
 * is what stops the board squashing when the window is resized: at a 990x557
 * frame an exact tile is the same 112x60 box at k = 0.773, type and all.
 *
 * The frame element has no transition. Since B30 the frame is derived from the
 * STAGE rather than measured off the clip, so it moves only when the container
 * does — and that move must be instant rather than a visible slide across the
 * green.
 */
export function ArcOverlay(props: ArcOverlayProps) {
  const driftRef = useRef<HTMLDivElement>(null)
  useBoardParallax(driftRef)
  const live = props.liveStatus ?? IDLE_LIVE_STATUS
  // Capped at the round's own three putts, and never negative: the stylesheet
  // spends this as an opacity step per plane, so an out-of-range value would
  // simply pin every plane on and the weather would arrive at full depth.
  const weatherStep = Math.max(0, Math.min(PUTTS_PER_ROUND, Math.floor(props.weatherStep ?? 0)))

  /**
   * B40 — the biggest tier the PLAYER actually won this round, or none.
   *
   * Derived here rather than passed: the overlay already holds every winning
   * bet, and a prop would be a second copy of a fact that is one `get` away. It
   * is the player's own win and not the round's — a 118x option nobody was on is
   * the round being generous to somebody else, and the board does not bloom for
   * it.
   *
   * `forEach` rather than `for…of`: a `Set` iterator needs `downlevelIteration`
   * at this build's target.
   */
  let topTier: WinTier | undefined
  props.winnerIds.forEach((optionId) => {
    const bet = props.betByOptionId.get(optionId)
    if (bet === undefined || !bet.won) return
    if (bet.tier === 'mega') topTier = 'mega'
    else if (bet.tier === 'big' && topTier === undefined) topTier = 'big'
  })
  const { frame, geometry, tiles } = props
  const viewByKey = new Map(tiles.map((tile) => [tile.key, tile]))
  const optionIndex = new Map(tiles.map((tile, index) => [tile.optionId, index]))

  /**
   * B40 — the two board-wide beats, as a delay per PLACEMENT.
   *
   * Ranked on the real tile boxes the geometry emits, so the cascade is the
   * board opening out of its own middle rather than a walk down the options
   * array — the two columns interleave, and the array order would have read as a
   * diagonal wipe. `boardMotion` does the ranking; ties share a rank, which is
   * what makes a symmetric board open symmetrically.
   */
  // B40 — a round that revealed a lightning boost. It is the game's marquee
  // surprise and it arrived as a seal on a corner; the board now answers it
  // once, at the frame's scale. Derived rather than passed, like the tier below:
  // the views already say which options were boosted.
  let struck = false
  for (const tile of tiles) {
    if (tile.boosted) struck = true
  }

  const points: BoardPoint[] = geometry.tiles.map((placement) => ({
    x: placement.centreX,
    y: placement.centreY
  }))
  const middle = centreOf(points)
  const enterDelays = cascadeDelaysMs(points, middle, TILE_ENTER_STEP_MS)
  // The dim wave runs out of the WINNER once there is one, and out of the middle
  // while the putts are still ruling options out.
  let waveOrigin = middle
  for (let index = 0; index < geometry.tiles.length; index++) {
    const placement = geometry.tiles[index]
    const point = points[index]
    if (placement === undefined || point === undefined) continue
    const view = viewByKey.get(placement.key)
    if (view !== undefined && props.winnerIds.has(view.optionId)) {
      waveOrigin = point
      break
    }
  }
  const waveDelays = cascadeDelaysMs(points, waveOrigin, TILE_WAVE_STEP_MS)

  return (
    <div className="birdie-overlay">
      <div
        className="birdie-overlay__frame"
        style={
          {
            left: frame.x,
            top: frame.y,
            // The LAYOUT frame's own size, so `scale(k)` from the top-left
            // corner lands the box exactly on the picture. Fixed at
            // 1280x720 it covered only the reference-sized corner of every
            // wider one, and the board's chrome fractions described pixels that
            // were not under it.
            width: geometry.frame.width,
            height: geometry.frame.height,
            '--birdie-k': props.scale
          } as CSSProperties
        }
      >
        {/*
          B40 — THE SEAT (owner, on Evolution: "could we try test that board
          seated in the scene … when the round starts").

          The board tips a few degrees into the green while the putts are being
          played and lies flat again the moment the round is over. WHEN is the
          whole safety argument: a tilt moves the outer columns several px off the
          positions `boardGeometry` computes against the picture, which is
          unacceptable while the tiles are TARGETS a finger is aiming at — and
          costs nothing once they are only being read. So it rides the same live
          window the focus pull does, and it is a class on a declarative
          transform rather than an accumulating animation: there is no state in
          which a second round starts from anywhere but exactly flat.

          It is its own element and not the frame's transform for a reason
          written on `__frame` above: the frame must NEVER carry a transition on
          `transform`, because its scale changes with the window and a window
          resize would become a visible slide across the green. This element's
          only transform is the tilt, so it can be transitioned safely.
        */}
        {/*
          The gradients every ball on this board is painted with. One zero-sized
          <svg> for the whole overlay — the tiles, the capsule's score slots and
          the past-rounds rows are all inside it, and the ids are fixed and
          shared, so a second copy anywhere else in the document is a no-op
          (`GolfBall.tsx`).
        */}
        <BirdieBallDefs />

        {/*
          B40 item 8 — the round's weather, on the board's edges.

          FIRST in the frame, and at z-index 0, so it paints behind every tile,
          every header and the capsule: the stream's own frost or fire ties to the
          UI without a pixel of it landing on a price. It is mounted only for the
          two types that have a look, and it fades itself out — a new round takes
          the type back to undefined, which is what arms it again.
        */}
        {/*
          B40 — a big win reaches the FRAME. The shared `<WinPill>` carries the
          tiers, and Birdie pins its scale to 1 because a 42-px bar has nowhere
          to grow into, so the board's own response is where the escalation
          lives: the tile's shockwave is thrown further, and at these two tiers
          the picture's edges bloom with it, once.

          Outside the seat and the drift, like the weather: it belongs to the
          frame, not to the options.
        */}
        {topTier === undefined ? null : (
          <div
            aria-hidden="true"
            className={`birdie-board__flare birdie-board__flare--${topTier}`}
          />
        )}

        {/*
          B40 — THE STRIKE. The boost is revealed at the close, all at once, and
          until now the whole event was a gold disc appearing on a corner. One
          pass of light across the picture says it happened; the seal landing and
          the boosted tile's own charge say where.

          Outside the seat and the drift, like the weather and the win flare: it
          belongs to the frame. Behind everything, and gone.
        */}
        {struck ? <div aria-hidden="true" className="birdie-board__strike" /> : null}

        {props.roundType ? (
          <div
            aria-hidden="true"
            className={`birdie-board__weather birdie-board__weather--${props.roundType}`}
            style={{ '--birdie-weather': String(weatherStep) } as CSSProperties}
          />
        ) : null}

        {/*
          The two header lines — the ONLY text on the board (B20). Each is a
          label, not a button: no pill, no background, no hover, and no caption
          anywhere else. The BOX comes from `boardGeometry.headers`, which is
          also what the columns were resolved against: the header is what a
          grid's `top` is, and the first row hangs `header.gap` plus `pillBand`
          under it — the band that keeps a settled <WinPill> off the label.

          The icon sits on the OUTER side of each label — left of "Exact order"
          on the left column, right of "Any order" on the right — so the two
          headers frame the board rather than pointing into it. `align` is the
          same value that decided which edge the box hangs off.
        */}
        {/*
          B40 — THE SEAT WRAPS THE OPTIONS, AND NOTHING ELSE (owner, watching it:
          "probably tilt should only really be on the betting options if that's
          possible").

          It used to wrap the frame's whole contents, which made the board read
          as the UI leaning rather than as a table seating itself. What is
          deliberately OUTSIDE it now:
           - the WEATHER, which is the round's atmosphere on the frame's own
             edges. It is registered to the frame; tilting it drags it off the
             thing it is drawn on and breaks the illusion outright.
           - the CAPSULE, the notice and the callout, which are chrome ABOUT the
             round rather than part of the board being played.
          What is inside: the tiles, and their two column headers. The headers go
          with them on purpose — each is a label plus a hairline that runs the
          length of its own column, so a flat header over a tilted column would
          read as the label having come off the board. They also sit within a few
          px of the pivot, so the tilt costs them almost nothing.

          The wrapper is `inset: 0` with no layout of its own, so every tile is
          exactly where `boardGeometry` placed it — the geometry is relative to
          the frame and this element adds no box between them.
        */}
        {/*
          TWO wrappers, both `inset: 0` with no layout of their own, both around
          the options and nothing else. They are separate elements for one
          reason: the tilt is TRANSITIONED (0.75 s, it is a move the eye should
          follow) and the drift must be INSTANT (it answers a pointer), and a
          single `transform` declaration cannot be both. The individual
          `translate`/`rotate` properties would allow it on one element; they are
          not safe at this build's target.
        */}
        <div className="birdie-overlay__drift" ref={driftRef}>
          <div
            className={`birdie-overlay__seat${props.seated === true ? ' birdie-overlay__seat--tilted' : ''}`}
          >
            {geometry.headers.map((header) => (
              <div
                // B40 item 9: the rail carries a slow specular sweep while betting is
                // open, and nothing at all once the tiles lock — idle life belongs to
                // the part of the round where the player is deciding.
                className={`birdie-board__head birdie-board__head--${HEAD_MODIFIER[header.column]}${
                  props.tilesLocked ? '' : ' birdie-board__head--live'
                }`}
                key={header.column}
                style={
                  {
                    left: header.left,
                    top: header.top,
                    width: header.width,
                    height: header.height,
                    justifyContent: header.align === 'end' ? 'flex-end' : 'flex-start'
                  } as CSSProperties
                }
              >
                {header.align === 'end' ? (
                  <>
                    <span aria-hidden="true" className="birdie-board__head-rule" />
                    <span className="birdie-board__head-label">
                      <span className="birdie-board__head-text">
                        {props.headings[header.column]}
                      </span>
                      <BoardHeadIcon column={header.column} />
                    </span>
                  </>
                ) : (
                  <>
                    <span className="birdie-board__head-label">
                      <BoardHeadIcon column={header.column} />
                      <span className="birdie-board__head-text">
                        {props.headings[header.column]}
                      </span>
                    </span>
                    <span aria-hidden="true" className="birdie-board__head-rule" />
                  </>
                )}
              </div>
            ))}

            {geometry.tiles.map((placement, placementIndex) => {
              const view = viewByKey.get(placement.key)
              if (!view) return null
              const chip = props.placement[view.optionId]
              const bet = props.betByOptionId.get(view.optionId)
              const index = optionIndex.get(view.optionId) ?? 0
              return (
                <BirdieTile
                  beat={props.puttsSeen ?? 0}
                  chipLabel={props.chipLabel}
                  {...(chip ? { chipStyle: props.chipStyleForAmount(chip.amount) } : {})}
                  currencySymbol={props.currencySymbol}
                  enterDelayMs={enterDelays[placementIndex] ?? 0}
                  enterKey={props.enterKey}
                  freeBet={props.freeBetIds?.has(view.optionId) === true}
                  freeBetLabel={props.freeBetLabel}
                  key={view.optionId}
                  locked={props.tilesLocked}
                  life={tileLife(live, view.optionId)}
                  hot={live.hotIds.has(view.optionId)}
                  myBetLost={bet?.lost === true}
                  myBetWon={bet?.won === true}
                  nearMiss={live.nearMissIds.has(view.optionId)}
                  onLockedPress={props.onLockedPress}
                  onPlace={props.onPlace}
                  onRemove={props.onRemove}
                  // B36: the side is the COLUMN INDEX, not the family. `count` used to be one column of
                  // bars, so a pill could always hang right; it is two tiles wide now, and a col-0 pill
                  // hanging right would land on top of its neighbour. Both families read the same way:
                  // the inner tile's pill goes left into the gap between the columns, the outer one's
                  // goes right.
                  pillSide={pillSideFor(placement)}
                  placedAmount={chip?.amount}
                  placedCount={chip?.count ?? 0}
                  receiving={props.receivingOptionId === view.optionId}
                  shineIndex={index}
                  stakeDescription={props.stakeDescription}
                  style={
                    {
                      left: placement.left,
                      top: placement.top,
                      width: placement.width,
                      height: placement.height
                    } as CSSProperties
                  }
                  view={view}
                  waveDelayMs={waveDelays[placementIndex] ?? 0}
                  winDelayMs={index * WIN_STAGGER_MS}
                  winPayout={bet?.payout ?? 0}
                  winTier={bet?.tier ?? 'win'}
                  winner={props.winnerIds.has(view.optionId)}
                  zoneMiss={props.zoneMissIds?.has(view.optionId) === true}
                />
              )
            })}
          </div>
        </div>

        {props.notice ? (
          <div
            aria-live="polite"
            className={`birdie-notice birdie-notice--${props.notice.tone} birdie-notice--persistent`}
            role="status"
            style={
              {
                left: geometry.frame.width / 2,
                top: geometry.holePx.y - NOTICE_LIFT_PX
              } as CSSProperties
            }
          >
            {props.notice.message}
          </div>
        ) : null}

        {props.children}
      </div>
    </div>
  )
}
